<template>
  <div class="whatsapp-preview">
    <div class="phone-frame">
      <!-- Phone Header -->
      <div class="phone-status-bar">
        <span class="time">9:41</span>
        <div class="status-icons">
          <svg width="15" height="11" viewBox="0 0 15 11" fill="currentColor">
            <path d="M0 9h2v2H0zm4 0h2v2H4zm4 0h2v2H8zm5-9L1 11l1.5 1.5L15 1z"/>
          </svg>
          <svg width="17" height="11" viewBox="0 0 17 11" fill="currentColor">
            <path d="M15 0H2C.9 0 0 .9 0 2v7c0 1.1.9 2 2 2h13c1.1 0 2-.9 2-2V2c0-1.1-.9-2-2-2zm0 2v7H2V2h13z"/>
          </svg>
        </div>
      </div>

      <!-- WhatsApp Header -->
      <div class="whatsapp-header">
        <button class="back-btn">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
        </button>
        <div class="contact-avatar">
          <div class="avatar-circle"></div>
        </div>
        <div class="contact-info">
          <div class="contact-name">{{ screenTitle }}</div>
          <div class="contact-status">Managed by the business</div>
        </div>
        <button class="menu-btn">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
          </svg>
        </button>
      </div>

      <!-- Chat Area -->
      <div class="chat-area">
        <div class="message-bubble incoming">
          <div class="flow-content">
            <!-- Render each component -->
            <div v-for="(component, index) in components" :key="index" class="flow-component">
              <!-- TextHeading -->
              <h2 v-if="component.type === 'TextHeading'" class="text-heading">
                {{ component.text }}
              </h2>

              <!-- TextSubheading -->
              <h3 v-else-if="component.type === 'TextSubheading'" class="text-subheading">
                {{ component.text }}
              </h3>

              <!-- TextBody -->
              <p v-else-if="component.type === 'TextBody'" class="text-body">
                {{ component.text }}
              </p>

              <!-- TextCaption -->
              <p v-else-if="component.type === 'TextCaption'" class="text-caption">
                {{ component.text }}
              </p>

              <!-- Image -->
              <div v-else-if="component.type === 'Image'" class="image-component">
                <img v-if="component.src" :src="component.src" :alt="component.alt || 'Image'" />
                <div v-else class="image-placeholder">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                  </svg>
                  <span>Image</span>
                </div>
              </div>

              <!-- TextInput -->
              <div v-else-if="component.type === 'TextInput'" class="input-component">
                <label class="input-label">{{ component.label }}</label>
                <input
                  type="text"
                  :placeholder="component.label"
                  :required="component.required"
                  class="text-input"
                  disabled
                />
              </div>

              <!-- TextArea -->
              <div v-else-if="component.type === 'TextArea'" class="input-component">
                <label class="input-label">{{ component.label }}</label>
                <textarea
                  :placeholder="component.label"
                  :required="component.required"
                  class="text-area"
                  rows="3"
                  disabled
                ></textarea>
              </div>

              <!-- CheckboxGroup -->
              <div v-else-if="component.type === 'CheckboxGroup'" class="input-component">
                <label class="input-label">{{ component.label }}</label>
                <div class="checkbox-group">
                  <div v-for="option in component['data-source']" :key="option.id" class="checkbox-item">
                    <input type="checkbox" :id="option.id" disabled />
                    <label :for="option.id">{{ option.title }}</label>
                  </div>
                  <div v-if="!component['data-source'] || component['data-source'].length === 0" class="empty-options">
                    <span>No options defined</span>
                  </div>
                </div>
              </div>

              <!-- RadioButtonsGroup -->
              <div v-else-if="component.type === 'RadioButtonsGroup'" class="input-component">
                <label class="input-label">{{ component.label }}</label>
                <div class="radio-group">
                  <div v-for="option in component['data-source']" :key="option.id" class="radio-item">
                    <input type="radio" :name="component.name" :id="option.id" disabled />
                    <label :for="option.id">{{ option.title }}</label>
                  </div>
                  <div v-if="!component['data-source'] || component['data-source'].length === 0" class="empty-options">
                    <span>No options defined</span>
                  </div>
                </div>
              </div>

              <!-- Dropdown -->
              <div v-else-if="component.type === 'Dropdown'" class="input-component">
                <label class="input-label">{{ component.label }}</label>
                <select class="dropdown" disabled>
                  <option value="">{{ component.label }}</option>
                  <option v-for="option in component['data-source']" :key="option.id" :value="option.id">
                    {{ option.title }}
                  </option>
                  <option v-if="!component['data-source'] || component['data-source'].length === 0" disabled>
                    No options defined
                  </option>
                </select>
              </div>

              <!-- DatePicker -->
              <div v-else-if="component.type === 'DatePicker'" class="input-component">
                <label class="input-label">{{ component.label }}</label>
                <input type="date" class="date-input" disabled />
              </div>

              <!-- OptIn -->
              <div v-else-if="component.type === 'OptIn'" class="input-component">
                <div class="optin-item">
                  <input type="checkbox" :id="'optin-' + index" disabled />
                  <label :for="'optin-' + index">{{ component.label }}</label>
                </div>
              </div>

              <!-- PhotoPicker -->
              <div v-else-if="component.type === 'PhotoPicker'" class="input-component">
                <label class="input-label">{{ component.label }}</label>
                <button class="upload-btn" disabled>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                  </svg>
                  Upload Photo
                </button>
              </div>

              <!-- DocumentPicker -->
              <div v-else-if="component.type === 'DocumentPicker'" class="input-component">
                <label class="input-label">{{ component.label }}</label>
                <button class="upload-btn" disabled>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 2c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6H6zm7 7V3.5L18.5 9H13z"/>
                  </svg>
                  Upload Document
                </button>
              </div>

              <!-- EmbeddedLink -->
              <div v-else-if="component.type === 'EmbeddedLink'" class="embedded-link">
                <a href="#" @click.prevent>{{ component.text }}</a>
              </div>

              <!-- Form -->
              <div v-else-if="component.type === 'Form'" class="form-container">
                <div v-for="(child, childIndex) in component.children" :key="childIndex" class="form-child">
                  <!-- Recursively render form children - simplified for now -->
                  <div class="input-component">
                    <label class="input-label">{{ child.label || child.type }}</label>
                  </div>
                </div>
              </div>

              <!-- Footer (always at bottom) -->
              <div v-else-if="component.type === 'Footer'" class="footer-component">
                <button class="footer-btn">
                  {{ component.label }}
                </button>
                <div class="footer-caption">Managed by the business. <a href="#">Learn more</a></div>
              </div>

              <!-- Unknown component type -->
              <div v-else class="unknown-component">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
                  <path d="M11 15h2v2h-2zm0-8h2v6h-2zm1-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                </svg>
                <span>{{ component.type }}</span>
              </div>
            </div>

            <!-- Empty State -->
            <div v-if="!components || components.length === 0" class="empty-preview">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" opacity="0.2">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
              </svg>
              <p>Add components to see preview</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WhatsAppFlowPreview',
  props: {
    screenTitle: {
      type: String,
      default: 'Demo Screen'
    },
    components: {
      type: Array,
      default: () => []
    }
  }
}
</script>

<style scoped>
.whatsapp-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: var(--bg-secondary);
  height: 100%;
}

.phone-frame {
  width: 320px;
  height: 640px;
  background: #0a0a0a;
  border-radius: 32px;
  padding: 8px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.phone-status-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 16px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
}

.status-icons {
  display: flex;
  gap: 6px;
  align-items: center;
}

.whatsapp-header {
  background: #075e54;
  color: white;
  display: flex;
  align-items: center;
  padding: 8px;
  gap: 8px;
}

.back-btn,
.menu-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.contact-avatar {
  width: 36px;
  height: 36px;
}

.avatar-circle {
  width: 36px;
  height: 36px;
  background: #25d366;
  border-radius: 50%;
}

.contact-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.contact-name {
  font-size: 14px;
  font-weight: 600;
  color: white;
}

.contact-status {
  font-size: 11px;
  opacity: 0.8;
}

.chat-area {
  flex: 1;
  background: #e5ddd5;
  overflow-y: auto;
  padding: 12px;
  background-image:
    repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0, 0, 0, 0.02) 10px, rgba(0, 0, 0, 0.02) 20px);
}

.message-bubble {
  background: white;
  border-radius: 8px;
  padding: 0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.flow-content {
  padding: 12px;
}

.flow-component {
  margin-bottom: 12px;
}

.flow-component:last-child {
  margin-bottom: 0;
}

/* Text Components */
.text-heading {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 8px 0;
  color: #000;
}

.text-subheading {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #333;
}

.text-body {
  font-size: 14px;
  line-height: 1.4;
  margin: 0 0 8px 0;
  color: #000;
}

.text-caption {
  font-size: 12px;
  color: #667781;
  margin: 0;
}

/* Image Component */
.image-component img {
  width: 100%;
  border-radius: 4px;
  display: block;
}

.image-placeholder {
  background: #f0f2f5;
  border-radius: 4px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #667781;
  font-size: 12px;
}

/* Input Components */
.input-component {
  margin-bottom: 12px;
}

.input-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #000;
}

.text-input,
.text-area,
.dropdown,
.date-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d7db;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  background: white;
  color: #000;
}

.text-input:focus,
.text-area:focus,
.dropdown:focus,
.date-input:focus {
  outline: none;
  border-color: #25d366;
}

.text-area {
  resize: vertical;
  min-height: 80px;
}

/* Checkbox and Radio */
.checkbox-group,
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checkbox-item,
.radio-item,
.optin-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.checkbox-item input,
.radio-item input,
.optin-item input {
  width: 18px;
  height: 18px;
  accent-color: #25d366;
}

.checkbox-item label,
.radio-item label,
.optin-item label {
  font-size: 14px;
  color: #000;
  cursor: pointer;
}

.empty-options {
  padding: 8px;
  background: #f0f2f5;
  border-radius: 4px;
  text-align: center;
  font-size: 12px;
  color: #667781;
}

/* Upload Buttons */
.upload-btn {
  width: 100%;
  padding: 12px;
  background: #f0f2f5;
  border: 1px dashed #d1d7db;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  color: #667781;
  cursor: pointer;
  transition: all 0.2s;
}

.upload-btn:hover {
  background: #e4e6eb;
  border-color: #25d366;
  color: #25d366;
}

/* Embedded Link */
.embedded-link a {
  color: #00a5f4;
  text-decoration: none;
  font-size: 14px;
}

.embedded-link a:hover {
  text-decoration: underline;
}

/* Footer Component */
.footer-component {
  margin-top: 16px;
  margin-bottom: 0;
}

.footer-btn {
  width: 100%;
  padding: 14px;
  background: #25d366;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.footer-btn:hover {
  background: #20ba5a;
}

.footer-caption {
  text-align: center;
  font-size: 11px;
  color: #667781;
  margin-top: 8px;
}

.footer-caption a {
  color: #00a5f4;
  text-decoration: none;
}

/* Empty State */
.empty-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  color: #667781;
}

.empty-preview p {
  margin-top: 12px;
  font-size: 14px;
}

/* Unknown Component */
.unknown-component {
  background: #f0f2f5;
  border-radius: 6px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #667781;
  font-size: 12px;
}

/* Scrollbar */
.chat-area::-webkit-scrollbar {
  width: 6px;
}

.chat-area::-webkit-scrollbar-track {
  background: transparent;
}

.chat-area::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.chat-area::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}
</style>
